import { useState, useRef, useEffect, useMemo, useCallback } from "react";

interface Props {
  selection: string;
  entries: string[];
  onSelection: (e: string) => void;
}

enum Mode {
  Normal,
  Expanded,
  Fixed,
}

const TableDropdown = (props: Props) => {
  const [mode, setMode] = useState(props.selection === "" ? Mode.Normal : Mode.Fixed);
  const [stringFilter, setStringFilter] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Memoize filtered entries for performance
  const filteredEntries = useMemo(
    () => props.entries.filter((e) => e.toLowerCase().includes(stringFilter.toLowerCase())),
    [props.entries, stringFilter]
  );

  // Helper to reset mode
  const resetMode = useCallback(() => {
    setMode(props.selection === "" ? Mode.Normal : Mode.Fixed);
  }, [props.selection]);

  // Focus input when dropdown expands
  useEffect(() => {
    if (mode === Mode.Expanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [mode]);

  // Click outside & Escape handling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        resetMode();
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") resetMode();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [resetMode]);

  // Open dropdown
  const openDropdown = () => setMode(Mode.Expanded);

  // Select entry
  const selectEntry = (entry: string) => {
    props.onSelection(entry);
    setStringFilter("");
    setMode(Mode.Fixed);
  };

  // Handle keyboard navigation inside the input
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      setHighlightedIndex((prev) => Math.min(prev + 1, filteredEntries.length - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
      e.preventDefault();
    } else if (e.key === "Enter") {
      if (filteredEntries[highlightedIndex]) {
        selectEntry(filteredEntries[highlightedIndex]);
      }
      e.preventDefault();
    }
  };

  if (mode === Mode.Fixed) {
    return (
      <button
        onClick={openDropdown}
        className="block text-left text-gray-600 hover:underline truncate w-full max-w-full"
        aria-expanded={false}
      >
        {props.selection}
      </button>
    );
  }

  return (
    <div ref={dropdownRef} className="relative w-full max-w-full">
      <button
        onClick={openDropdown}
        className="mt-1 px-2 bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white rounded box-border w-full max-w-full"
        aria-expanded={mode === Mode.Expanded}
      >
        <div className="flex items-center gap-2 w-full">
          <span className="shrink-0">{"\u25BC"}</span>
          <span className="h-4 w-px bg-white/70 shrink-0" />
          <span className="truncate">{props.selection === "" ? "–" : props.selection}</span>
        </div>
      </button>

      {mode === Mode.Expanded && (
        <div
          className="absolute top-7 z-10 bg-gray-50 border border-slate-300 rounded box-border w-full max-w-full"
          role="listbox"
        >
          <input
            ref={inputRef}
            type="text"
            placeholder="Search..."
            className="w-full px-1 py-1 text-gray-700 border-b border-slate-300 focus:outline-none"
            onChange={(e) => {
              setStringFilter(e.currentTarget.value);
              setHighlightedIndex(0); // reset highlight on filter change
            }}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />
          <div className="max-h-60 overflow-y-auto">
            {filteredEntries.length > 0 ? (
              filteredEntries.map((e, i) => (
                <button
                  key={i}
                  role="option"
                  aria-selected={highlightedIndex === i}
                  className={`block w-full px-1 py-1 text-left text-gray-700 hover:bg-slate-100 ${
                    highlightedIndex === i ? "bg-slate-200" : ""
                  }`}
                  onClick={() => selectEntry(e)}
                  onMouseEnter={() => setHighlightedIndex(i)}
                >
                  {e}
                </button>
              ))
            ) : (
              <div className="px-1 py-1 text-gray-400">No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TableDropdown;
